import React, { useEffect, useState } from 'react';
import { post, put, get, getWithParams } from "../../utils/fetchAPI";
import { toast } from "react-toastify";
import { FaTimes, FaPlus } from 'react-icons/fa';
import Select from 'react-select';

const FollowupTestCreate = ({ test, createTest, fetchTests, onClose }) => {
    const [testData, setTestData] = useState({
        name: '',
        order: 1,
        active: true,
        doctor: [],
        breakdowns: [],
    });
    const [doctors, setDoctors] = useState([]);
    const [newBreakdown, setNewBreakdown] = useState({ name: '', description: '' });

    // Fetch doctors list
    useEffect(() => {
        const fetchDoctors = async () => {
            try {
                const response = await getWithParams('/doctor/list-v2', {
                    per_page: 100,
                    current_page: 1,
                });
                if (response?.status === 200) {
                    setDoctors(response.data?.doctors || []);
                }
            } catch (error) {
                console.error('Error fetching doctors:', error);
                toast.error('Failed to fetch doctors list', {
                    position: "top-center",
                    theme: "light",
                });
            }
        };
        fetchDoctors();
    }, []);

    useEffect(() => {
        if (test) {
            setTestData({
                name: test.name || '',
                order: test.order ?? 1,
                active: test.active !== undefined ? test.active : true,
                doctor: test.doctor || [],
                breakdowns: test.breakdowns || [],
            });
        } else {
            // Reset form for new test
            setTestData({
                name: '',
                order: 1,
                active: true,
                doctor: [],
                breakdowns: [],
            });
            setNewBreakdown({ name: '', description: '' });
        }
    }, [test]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setTestData({
            ...testData,
            [name]: type === 'checkbox' ? checked : type === 'number' ? (value === '' ? '' : Number(value)) : value,
        });
    };

    const handleAddBreakdown = () => {
        if (newBreakdown.name.trim()) {
            setTestData({
                ...testData,
                breakdowns: [...testData.breakdowns, { ...newBreakdown }],
            });
            setNewBreakdown({ name: '', description: '' });
        } else {
            toast.error('Breakdown name is required', {
                position: "top-center",
                theme: "light",
            });
        }
    };

    const handleRemoveBreakdown = (index) => {
        const updatedBreakdowns = testData.breakdowns.filter((_, i) => i !== index);
        setTestData({
            ...testData,
            breakdowns: updatedBreakdowns,
        });
    };

    const handleBreakdownChange = (index, field, value) => {
        const updatedBreakdowns = testData.breakdowns.map((bd, i) => {
            if (i === index) {
                return { ...bd, [field]: value };
            }
            return bd;
        });
        setTestData({
            ...testData,
            breakdowns: updatedBreakdowns,
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!testData.name.trim()) {
            toast.error('Test name is required!', {
                position: "top-center",
                theme: "light",
            });
            return;
        }

        const dataToSend = {
            name: testData.name.trim(),
            order: typeof testData.order === 'number' ? testData.order : Number(testData.order) || 1,
            active: testData.active,
            doctor: testData.doctor || [],
            breakdowns: testData.breakdowns,
        };

        try {
            if (test) {
                // Update existing test
                const response = await put(`/followup-test/${test._id}`, dataToSend);
                toast.success('Follow-up test updated successfully!', {
                    position: "top-center",
                    type: "success",
                    theme: "light",
                    autoClose: true,
                    hideProgressBar: true,
                    draggable: true,
                });
                createTest(response.data);
                await fetchTests();
                onClose();
            } else {
                // Create new test
                const response = await post('/followup-test/create', dataToSend);
                toast.success('Follow-up test created successfully!', {
                    position: "top-center",
                    theme: "light",
                    autoClose: true,
                    hideProgressBar: true,
                    draggable: true,
                });
                createTest(response.data);
                await fetchTests();
                onClose();
            }
        } catch (error) {
            console.error('Error saving follow-up test:', error);
            const errorMessage = error.response?.data?.errors?.[0]?.msg ||
                error.response?.data?.message ||
                'Failed to save follow-up test. Please try again.';
            toast.error(errorMessage, {
                position: "top-center",
                theme: "light",
                hideProgressBar: true,
            });
        }
    };

    return (
        <div className='py-6 min-[320px]:py-2 lg:px-5 min-[320px]:px-2'>
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-blue-500 lg:text-3xl sm:text-lg font-bold">
                    {test ? "Edit Follow-Up Test" : "Create New Follow-Up Test"}
                </h2>
                <button
                    onClick={onClose}
                    className="text-gray-500 hover:text-gray-700"
                >
                    <FaTimes className="w-6 h-6" />
                </button>
            </div>

            <form className='text-black lg:text-sm min-[320px]:text-xs flex flex-col w-full min-[320px]:max-h-[30rem] overflow-y-auto' onSubmit={handleSubmit}>
                {/* Test Name */}
                <div className="mb-4">
                    <label className="block mb-2 font-medium">Test Name *</label>
                    <input
                        type="text"
                        name="name"
                        value={testData.name}
                        onChange={handleInputChange}
                        placeholder="e.g., Urine RME, CBC, S.Creatinine"
                        className="w-full text-sm border p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                        required
                    />
                </div>

                {/* Order (for sorting) */}
                <div className="mb-4">
                    <label className="block mb-2 font-medium">Order</label>
                    <input
                        type="number"
                        name="order"
                        value={testData.order}
                        onChange={handleInputChange}
                        placeholder="1"
                        min={1}
                        className="w-full text-sm border p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    <p className="text-xs text-gray-500 mt-1">Lower numbers appear first when sorting by order</p>
                </div>

                {/* Doctor Selection */}
                <div className="mb-4">
                    <label className="block mb-2 font-medium">Tagged Doctors</label>
                    <Select
                        isMulti
                        options={doctors.map(doctor => ({
                            value: doctor._id,
                            label: doctor.name,
                        }))}
                        value={(() => {
                            // Handle both populated doctor objects and ID arrays
                            const doctorIds = testData.doctor.map(d =>
                                typeof d === 'string' ? d : d._id || d
                            );
                            return doctors
                                .filter(doctor => doctorIds.includes(doctor._id))
                                .map(doctor => ({
                                    value: doctor._id,
                                    label: doctor.name,
                                }));
                        })()}
                        onChange={(selectedOptions) => {
                            setTestData({
                                ...testData,
                                doctor: selectedOptions ? selectedOptions.map(opt => opt.value) : [],
                            });
                        }}
                        placeholder="Select doctors (leave empty for all doctors)..."
                        isClearable
                        isSearchable
                        className="text-sm"
                        styles={{
                            control: (provided) => ({
                                ...provided,
                                minHeight: '38px',
                            }),
                            multiValue: (provided) => ({
                                ...provided,
                                backgroundColor: '#e0f2f1',
                            }),
                            multiValueLabel: (provided) => ({
                                ...provided,
                                color: '#00695c',
                            }),
                        }}
                    />
                    <p className="text-xs text-gray-500 mt-1">Select specific doctors or leave empty to make test available for all doctors</p>
                </div>

                {/* Active Status */}
                <div className="mb-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            name="active"
                            checked={testData.active}
                            onChange={handleInputChange}
                            className="w-4 h-4 text-primary focus:ring-primary rounded"
                        />
                        <span className="font-medium">Active</span>
                    </label>
                    <p className="text-xs text-gray-500 mt-1">Active tests will be visible in follow-up sheets</p>
                </div>

                {/* Breakdowns Section */}
                <div className="mb-4">
                    <label className="block mb-2 font-medium">Breakdowns</label>
                    <p className="text-xs text-gray-500 mb-2">
                        Add breakdown items for hierarchical tests (e.g., ALB, P.Cell for Urine RME).
                        Leave empty for single-value tests.
                    </p>

                    {/* Existing Breakdowns */}
                    {testData.breakdowns.length > 0 && (
                        <div className="mb-3 space-y-2">
                            {testData.breakdowns.map((breakdown, index) => (
                                <div key={index} className="flex gap-2 items-center p-2 bg-gray-50 rounded border">
                                    <div className="flex-1 grid grid-cols-2 gap-2">
                                        <input
                                            type="text"
                                            value={breakdown.name || ''}
                                            onChange={(e) => handleBreakdownChange(index, 'name', e.target.value)}
                                            placeholder="Name (e.g., ALB)"
                                            className="text-sm border p-2 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
                                        />
                                        <input
                                            type="text"
                                            value={breakdown.description || ''}
                                            onChange={(e) => handleBreakdownChange(index, 'description', e.target.value)}
                                            placeholder="Description (e.g., Albumin)"
                                            className="text-sm border p-2 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveBreakdown(index)}
                                        className="p-2 text-red-500 hover:bg-red-50 rounded"
                                        title="Remove"
                                    >
                                        <FaTimes />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Add New Breakdown */}
                    <div className="flex gap-2 items-center p-2 bg-gray-100 rounded border">
                        <div className="flex-1 grid grid-cols-2 gap-2">
                            <input
                                type="text"
                                value={newBreakdown.name}
                                onChange={(e) => setNewBreakdown({ ...newBreakdown, name: e.target.value })}
                                placeholder="Name (e.g., ALB)"
                                className="text-sm border p-2 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
                            />
                            <input
                                type="text"
                                value={newBreakdown.description}
                                onChange={(e) => setNewBreakdown({ ...newBreakdown, description: e.target.value })}
                                placeholder="Description (e.g., Albumin)"
                                className="text-sm border p-2 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
                            />
                        </div>
                        <button
                            type="button"
                            onClick={handleAddBreakdown}
                            className="p-2 bg-primary text-white rounded hover:bg-teal-700"
                            title="Add Breakdown"
                        >
                            <FaPlus />
                        </button>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-4 mt-6 pt-4 border-t">
                    <button
                        type="submit"
                        className="text-sm px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
                    >
                        {test ? 'Update' : 'Create'}
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-sm px-6 py-2 bg-gray-300 text-black rounded-md hover:bg-gray-400 transition-colors"
                    >
                        Cancel
                    </button>
                </div>
            </form>
        </div>
    );
};

export default FollowupTestCreate;
